import { CalendarIconV2 } from "~/assets/icons";
import styles from "./PrimaryInput.module.scss";
import classNames from "classnames/bind";
import { useState, useRef, useEffect } from "react";

const cx = classNames.bind(styles);

const PrimaryInput = (props: {
  label?: string;
  placeholder?: string;
  value?: string;
  name: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  error?: boolean;
  errorMessage?: string;
  className?: string;
  disabled?: boolean;
  type?: string;
  inputStyle?: React.CSSProperties;
  showCalendarIcon?: boolean;
  maxLength?: number;
  autocomplete?: boolean;
}) => {
  const {
    label,
    placeholder,
    value,
    name,
    onChange,
    errorMessage,
    className,
    error,
    disabled,
    type = "text",
    inputStyle,
    showCalendarIcon = true,
    maxLength,
    autocomplete = false,
  } = props;

  const [showModal, setShowModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = useState({
    hour: "00",
    minute: "00",
  });
  const modalRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const isDateType = type === "date" || type === "time" || type === "datetime";

  useEffect(() => {
    if (value && isDateType) {
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        setSelectedDate(date);
        setSelectedTime({
          hour: date.getHours().toString().padStart(2, "0"),
          minute: date.getMinutes().toString().padStart(2, "0"),
        });
      }
    }
  }, [value, isDateType]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node)
      ) {
        setShowModal(false);
      }
    };

    if (showModal) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showModal]);

  const handleInputClick = () => {
    if (isDateType && !disabled) {
      setShowModal(true);
    }
  };

  const formatDisplayValue = () => {
    if (!selectedDate) return "";

    const dateStr = selectedDate.toLocaleDateString("en-US");

    if (type === "date") return dateStr;
    if (type === "time") return `${selectedTime.hour}:${selectedTime.minute}`;
    if (type === "datetime")
      return `${dateStr} ${selectedTime.hour}:${selectedTime.minute}`;

    return "";
  };

  const handleDateSelect = (date: Date) => {
    setSelectedDate(date);
    if (type === "date") {
      handleConfirm(date, selectedTime);
    }
  };

  const handleTimeChange = (hour: string, minute: string) => {
    setSelectedTime({ hour, minute });
  };

  const handleConfirm = (
    date: Date | null = selectedDate,
    time = selectedTime
  ) => {
    if (!date && type !== "time") return;

    let finalDate: Date;
    if (type === "time") {
      finalDate = new Date();
      finalDate.setHours(parseInt(time.hour), parseInt(time.minute), 0, 0);
    } else {
      finalDate = new Date(date!);
      if (type === "datetime") {
        finalDate.setHours(parseInt(time.hour), parseInt(time.minute), 0, 0);
      }
    }

    const syntheticEvent = {
      target: {
        name,
        value: finalDate.toISOString(),
      },
    } as React.ChangeEvent<HTMLInputElement>;

    onChange?.(syntheticEvent);
    setShowModal(false);
  };

  const renderCalendar = () => {
    const currentDate = selectedDate || new Date();
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const days = [];
    for (let i = 0; i < firstDay; i++) {
      days.push(
        <div key={`empty-${i}`} className={cx("calendar-day", "empty")} />
      );
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const isSelected =
        selectedDate &&
        date.getDate() === selectedDate.getDate() &&
        date.getMonth() === selectedDate.getMonth() &&
        date.getFullYear() === selectedDate.getFullYear();

      days.push(
        <div
          key={day}
          className={cx("calendar-day", { selected: isSelected })}
          onClick={() => handleDateSelect(date)}
        >
          {day}
        </div>
      );
    }

    return days;
  };

  const renderTimePicker = () => {
    const hours = Array.from({ length: 24 }, (_, i) =>
      i.toString().padStart(2, "0")
    );
    const minutes = Array.from({ length: 60 }, (_, i) =>
      i.toString().padStart(2, "0")
    );

    return (
      <div className={cx("time-picker")}>
        <div className={cx("time-column")}>
          <div className={cx("time-label")}>Hour</div>
          <div className={cx("time-scroll")}>
            {hours.map((hour) => (
              <div
                key={hour}
                className={cx("time-option", {
                  selected: selectedTime.hour === hour,
                })}
                onClick={() => handleTimeChange(hour, selectedTime.minute)}
              >
                {hour}
              </div>
            ))}
          </div>
        </div>
        <div className={cx("time-separator")}>:</div>
        <div className={cx("time-column")}>
          <div className={cx("time-label")}>Minute</div>
          <div className={cx("time-scroll")}>
            {minutes.map((minute) => (
              <div
                key={minute}
                className={cx("time-option", {
                  selected: selectedTime.minute === minute,
                })}
                onClick={() => handleTimeChange(selectedTime.hour, minute)}
              >
                {minute}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderModal = () => {
    if (!showModal) return null;

    const currentDate = selectedDate || new Date();

    return (
      <div className={cx("modal-overlay")}>
        <div className={cx("modal-content")} ref={modalRef}>
          {(type === "date" || type === "datetime") && (
            <>
              <div className={cx("modal-header")}>
                <button
                  className={cx("nav-button")}
                  onClick={() =>
                    setSelectedDate(
                      new Date(
                        currentDate.getFullYear(),
                        currentDate.getMonth() - 1
                      )
                    )
                  }
                >
                  ←
                </button>
                <div className={cx("month-year")}>
                  {currentDate.toLocaleDateString("en-US", {
                    month: "long",
                    year: "numeric",
                  })}
                </div>
                <button
                  className={cx("nav-button")}
                  onClick={() =>
                    setSelectedDate(
                      new Date(
                        currentDate.getFullYear(),
                        currentDate.getMonth() + 1
                      )
                    )
                  }
                >
                  →
                </button>
              </div>
              <div className={cx("calendar-weekdays")}>
                {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(
                  (day) => (
                    <div key={day} className={cx("weekday")}>
                      {day}
                    </div>
                  )
                )}
              </div>
              <div className={cx("calendar-grid")}>{renderCalendar()}</div>
            </>
          )}

          {(type === "time" || type === "datetime") && renderTimePicker()}

          <div className={cx("modal-actions")}>
            <button
              className={cx("btn-cancel")}
              onClick={() => setShowModal(false)}
            >
              Cancel
            </button>
            <button
              className={cx("btn-confirm")}
              onClick={() => handleConfirm()}
            >
              Confirm
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={cx("primary-input-container", className)}>
      {label && (
        <label htmlFor={name} className={cx("input-label")}>
          {label}
        </label>
      )}
      <div className={cx("input-wrapper")}>
        <input
          ref={inputRef}
          id={name}
          name={name}
          type="text"
          placeholder={placeholder}
          value={isDateType ? formatDisplayValue() : value ?? ""}
          onChange={isDateType ? undefined : onChange}
          onClick={handleInputClick}
          className={cx(
            "input-field",
            { error: !!errorMessage },
            { error: error },
            { disabled: disabled }
          )}
          style={inputStyle}
          disabled={disabled}
          readOnly={isDateType}
          maxLength={maxLength}
          autoComplete={autocomplete ? "on" : "off"}
        />
        {isDateType && showCalendarIcon && (
          <CalendarIconV2
            className={cx("calendar-icon")}
            onClick={handleInputClick}
          />
        )}
      </div>
      {errorMessage && <p className={cx("error-message")}>{errorMessage}</p>}
      {renderModal()}
    </div>
  );
};

export default PrimaryInput;
